import Button from "@bmi-digital/components/button";
import IconButton from "@bmi-digital/components/icon-button";
import ArrowForwardIcon from "@bmi-digital/components/icon/ArrowForward";
import DeleteIcon from "@bmi-digital/components/icon/Delete";
import Typography from "@bmi-digital/components/typography";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { Link } from "gatsby";
import React from "react";
import {
  ACTION_TYPES,
  Sample,
  useBasketContext
} from "../contexts/SampleBasketContext";
import { useSiteContext } from "./Site";
import {
  CartActions,
  CartImage,
  CartInfo,
  CloseButton,
  ImageContainer,
  InfoContainer,
  Product,
  ProductButtonContainer,
  ProductColour,
  ProductList,
  ProductSize,
  ProductTitle,
  StyledBasketDialogContainer,
  TopContainer
} from "./styles/SampleBasketDialogStyles";

const SampleBasketDialog = ({
  title,
  basketUrl,
  maximumSamples,
  toggleCart
}: {
  title: string;
  basketUrl: string;
  maximumSamples: number | null;
  toggleCart?: () => void;
}) => {
  const {
    basketState: { products: productsInBasket },
    basketDispatch
  } = useBasketContext();

  const { getMicroCopy } = useSiteContext();

  const removeFromBasket = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    payload: Sample
  ) => {
    event.stopPropagation();
    basketDispatch({
      type: ACTION_TYPES.BASKET_REMOVE,
      payload
    });
  };

  const closeDialogButtonLabel = getMicroCopy(microCopy.DIALOG_CLOSE);
  const removeSampleButtonLabel = getMicroCopy(
    microCopy.PDP_OVERVIEW_REMOVE_SAMPLE
  );
  const completeSampleOrderButtonLabel = getMicroCopy(
    microCopy.PDP_OVERVIEW_COMPLETE_SAMPLE_ORDER
  );

  const continueBrowsingButtonLabel = getMicroCopy(
    microCopy.PDP_OVERVIEW_CONTINUE_BROWSING
  );

  return (
    <StyledBasketDialogContainer data-testid={"shopping-cart-dialog"}>
      <TopContainer data-testid={"shopping-cart-dialog-header-container"}>
        <CloseButton
          accessibilityLabel={closeDialogButtonLabel}
          onClick={toggleCart}
          data-testid={"shopping-cart-dialog-close-button"}
          gtm={{
            id: "cta-click1",
            label: closeDialogButtonLabel
          }}
        />
        <Typography
          variant="h3"
          hasUnderline
          data-testid={"shopping-cart-dialog-title"}
        >
          {title}
        </Typography>

        <CartInfo data-testid={"shopping-cart-dialog-info"}>
          <p>
            {productsInBasket.length < (maximumSamples || 0)
              ? getMicroCopy(
                  microCopy.PDP_OVERVIEW_SAMPLE_DIALOG_MESSAGE
                ).replace(":N", `${maximumSamples}`)
              : getMicroCopy(
                  microCopy.PDP_OVERVIEW_SAMPLE_LIMIT_REACHED_MESSAGE
                )}
          </p>
          <p>
            {getMicroCopy(
              microCopy.PDP_OVERVIEW_SAMPLE_DELIVERY_DIALOG_MESSAGE
            )}
          </p>
        </CartInfo>
      </TopContainer>

      {/*Product List */}

      <ProductList data-testid={"shopping-cart-dialog-product-list"}>
        {productsInBasket.map((product) => (
          <Product
            key={product.code}
            data-testid={"shopping-cart-dialog-product"}
          >
            <ImageContainer>
              <CartImage src={product.image} />
            </ImageContainer>
            <InfoContainer>
              <ProductTitle
                variant="h6"
                data-testid={`shopping-cart-dialog-product-title-${replaceSpaces(
                  product.name
                )}`}
              >
                {product.name}
              </ProductTitle>
              <ProductColour
                variant="h6"
                data-testid={`shopping-cart-dialog-product-colour-${replaceSpaces(
                  product.colour
                )}`}
              >
                {product.colour}
              </ProductColour>
              <ProductSize
                data-testid={`shopping-cart-dialog-product-size-${replaceSpaces(
                  product.measurements
                )}`}
              >
                {product.measurements}
              </ProductSize>
            </InfoContainer>
            <ProductButtonContainer>
              <IconButton
                accessibilityLabel={removeSampleButtonLabel}
                variant="flat"
                onClick={(event) => removeFromBasket(event, product)}
                data-testid={`shopping-cart-dialog-remove-product-${replaceSpaces(
                  product.name
                )}`}
                gtm={{
                  id: "cta-click1",
                  label: removeSampleButtonLabel
                }}
              >
                <DeleteIcon />
              </IconButton>
            </ProductButtonContainer>
          </Product>
        ))}
      </ProductList>
      <CartActions data-testid={"shopping-cart-dialog-actions"}>
        <Button
          component={Link}
          to={basketUrl}
          endIcon={<ArrowForwardIcon />}
          data-testid={"shopping-cart-dialog-complete-order-button"}
          gtm={{
            id: "cta-click1",
            label: completeSampleOrderButtonLabel,
            action: basketUrl
          }}
        >
          {completeSampleOrderButtonLabel}
        </Button>
        {toggleCart && (
          <Button
            variant="outlined"
            onClick={() => toggleCart()}
            data-testid={"shopping-cart-dialog-continue-browsing-button"}
            gtm={{
              id: "cta-click1",
              label: continueBrowsingButtonLabel
            }}
          >
            {continueBrowsingButtonLabel}
          </Button>
        )}
      </CartActions>
    </StyledBasketDialogContainer>
  );
};

export default SampleBasketDialog;
